// MHM-5 — dark/light/system theme context.
// Default is 'light' so the MHM portal matches the rest of PickNGo on first
// load. A one-time migration (`pickngo-theme-migrated == v2`) resets any
// previously-saved 'dark'/'system' choice back to 'light'. Users can still
// opt-in to dark/system via the toggle in MHMSubNav.
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const ThemeCtx = createContext({ theme: "light", effective: "light", setTheme: () => {}, cycle: () => {} });
const KEY = "pickngo-theme";
const MIGRATION_KEY = "pickngo-theme-migrated";
const MIGRATION_VERSION = "v2";

function resolve(theme) {
  if (theme === "dark") return "dark";
  if (theme === "light") return "light";
  return typeof window !== "undefined"
    && window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function readInitialTheme() {
  if (typeof window === "undefined") return "light";
  try {
    if (localStorage.getItem(MIGRATION_KEY) !== MIGRATION_VERSION) {
      localStorage.setItem(KEY, "light");
      localStorage.setItem(MIGRATION_KEY, MIGRATION_VERSION);
      return "light";
    }
    return localStorage.getItem(KEY) || "light";
  } catch {
    return "light";
  }
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readInitialTheme);
  const [effective, setEffective] = useState(() => resolve(theme));

  const apply = useCallback((t) => {
    const eff = resolve(t);
    setEffective(eff);
    if (typeof document !== "undefined") {
      document.documentElement.classList.toggle("dark", eff === "dark");
      document.documentElement.setAttribute("data-theme", eff);
    }
  }, []);

  useEffect(() => { apply(theme); }, [theme, apply]);

  // listen for system pref change while in 'system' mode
  useEffect(() => {
    if (theme !== "system" || typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => apply("system");
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, [theme, apply]);

  const setTheme = useCallback((t) => {
    setThemeState(t);
    try { localStorage.setItem(KEY, t); } catch { /* ignore */ }
  }, []);

  const cycle = useCallback(() => {
    setTheme(theme === "light" ? "dark" : theme === "dark" ? "system" : "light");
  }, [theme, setTheme]);

  const value = useMemo(() => ({ theme, effective, setTheme, cycle }),
                         [theme, effective, setTheme, cycle]);
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export function useTheme() { return useContext(ThemeCtx); }
